import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { categoryService, paramService } from '@/services/storage';
import type { ProductCategory, ProductParam } from '@/types';
import { toast } from 'sonner';

const paramTypes = [
  { value: 'text', label: '文本' },
  { value: 'number', label: '数字' },
  { value: 'boolean', label: '布尔' },
  { value: 'select', label: '下拉选择' },
];

export default function AdminParams() {
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [params, setParams] = useState<ProductParam[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingParam, setEditingParam] = useState<ProductParam | null>(null);
  const [formData, setFormData] = useState({
    key: '',
    label: '',
    unit: '',
    type: 'text' as 'text' | 'number' | 'boolean' | 'select',
  });

  useEffect(() => {
    const loadCategories = async () => {
      setCategories(await categoryService.getAll());
    };
    loadCategories();
  }, []);

  useEffect(() => {
    const loadParams = async () => {
      if (selectedCategory) {
        setParams(await paramService.getByCategory(selectedCategory));
      } else {
        setParams([]);
      }
    };
    loadParams();
  }, [selectedCategory]);

  const handleAdd = async () => {
    if (!selectedCategory) {
      toast.error('请先选择产品分类');
      return;
    }
    setEditingParam(null);
    setFormData({ key: '', label: '', unit: '', type: 'text' });
    setDialogOpen(true);
  };

  const handleEdit = async (param: ProductParam) => {
    setEditingParam(param);
    setFormData({
      key: param.key,
      label: param.label,
      unit: param.unit || '',
      type: param.type,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (key: string) => {
    if (confirm('确定要删除此参数吗？')) {
      const newParams = params.filter((p) => p.key !== key);
      await paramService.save(selectedCategory, newParams);
      setParams(newParams);
      toast.success('参数已删除');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.key || !formData.label) {
      toast.error('请填写必填项');
      return;
    }

    const newParam: ProductParam = {
      key: formData.key,
      label: formData.label,
      unit: formData.unit || undefined,
      type: formData.type,
    };

    let newParams: ProductParam[];
    if (editingParam) {
      newParams = params.map((p) => (p.key === editingParam.key ? newParam : p));
      toast.success('参数已更新');
    } else {
      newParams = [...params, newParam];
      toast.success('参数已添加');
    }

    await paramService.save(selectedCategory, newParams);
    setParams(newParams);
    setDialogOpen(false);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">参数管理</h1>
        <Button onClick={handleAdd}>
          <Plus className="mr-2 h-5 w-5" />
          添加参数
        </Button>
      </div>

      {/* Category Selector */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <Label>选择产品分类</Label>
          <select
            className="w-full border rounded-md px-3 py-2 mt-2"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">请选择分类</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </CardContent>
      </Card>

      {/* Params Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>参数键名</TableHead>
                <TableHead>参数名称</TableHead>
                <TableHead>单位</TableHead>
                <TableHead>类型</TableHead>
                <TableHead className="text-right">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {params.map((param) => (
                <TableRow key={param.key}>
                  <TableCell className="font-medium">{param.key}</TableCell>
                  <TableCell>{param.label}</TableCell>
                  <TableCell>{param.unit || '-'}</TableCell>
                  <TableCell>
                    {paramTypes.find((t) => t.value === param.type)?.label || param.type}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(param)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(param.key)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {params.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              {selectedCategory ? '该分类暂无参数定义' : '请先选择产品分类'}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingParam ? '编辑参数' : '添加参数'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="key">
                参数键名 <span className="text-red-500">*</span>
              </Label>
              <Input
                id="key"
                value={formData.key}
                onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                placeholder="如iout, vin"
                disabled={!!editingParam}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                用于系统识别，建议使用英文小写
              </p>
            </div>
            <div>
              <Label htmlFor="label">
                参数名称 <span className="text-red-500">*</span>
              </Label>
              <Input
                id="label"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                placeholder="如输出电流"
                required
              />
            </div>
            <div>
              <Label htmlFor="unit">单位</Label>
              <Input
                id="unit"
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                placeholder="如mA, V"
              />
            </div>
            <div>
              <Label htmlFor="type">类型</Label>
              <select
                id="type"
                className="w-full border rounded-md px-3 py-2"
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
              >
                {paramTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                取消
              </Button>
              <Button type="submit">
                <Save className="mr-2 h-4 w-4" />
                保存
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
